import React, { useState, useMemo, useEffect } from 'react';
import { ShoppingCart, Heart, Eye, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useFavorites } from '../../context/FavoritesContext';
import Card from '../common/Card';
import Button from '../common/Button';
import Toast from '../common/Toast';
//import api from '../../services/api';
import productService from '../../services/productService';

const CategoriesPage = () => {
    const { addToCart } = useCart();
    const { toggleFavorite, isFavorite } = useFavorites();
    const [products, setProducts] = useState([]);
    const [categoriesList, setCategoriesList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const itemsPreviewCount = 3;

    useEffect(() => {
        const loadData = async () => {
            try {
                const [productsData, categoriesData] = await Promise.all([
                    productService.getProducts(),
                    productService.getCategories()
                ]);
                setProducts(productsData);
                setCategoriesList(categoriesData);
                setLoading(false);
            } catch (err) {
                console.error('Error fetching data:', err);
                setError('Impossible de charger les collections. Veuillez réessayer plus tard.');
                setLoading(false);
            }
        };

        loadData();
    }, []);

    // Group products by category
    const productsByCategory = useMemo(() => {
        return products.reduce((acc, product) => {
            if (!acc[product.category]) {
                acc[product.category] = [];
            }
            acc[product.category].push(product);
            return acc;
        }, {});
    }, [products]);

    const categories = Object.keys(productsByCategory);

    const handleAddToCart = (product) => {
        const wasAdded = addToCart(product);
        if (wasAdded) {
            setToastMessage("Produit ajouté au panier !");
        } else {
            setToastMessage("Ce produit est déjà dans votre panier.");
        }
        setShowToast(true);
    };



    return (
        <div className="pt-32 pb-24 bg-slate-50 min-h-screen">
            <div className="container mx-auto px-6">
                {/* Header */}
                <div className="max-w-3xl mb-16">
                    <h1 className="text-sm font-bold tracking-[0.2em] text-brand uppercase mb-4">Explorateur de Collections</h1>
                    <h2 className="text-4xl md:text-6xl font-extrabold text-brand-dark leading-tight mb-8">
                        Parcourez l'Art de Vivre par Catégorie
                    </h2>
                    <p className="text-lg text-brand-dark/60 leading-relaxed">
                        Chaque espace mérite une attention particulière. Découvrez nos sélections exclusives conçues pour élever chaque recoin de votre demeure.
                    </p>
                </div>

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-32 space-y-6">
                        <div className="w-16 h-16 border-4 border-brand/20 border-t-brand rounded-full animate-spin" />
                        <p className="text-brand-dark/60 font-medium animate-pulse">Chargement des collections...</p>
                    </div>
                ) : error ? (
                    <div className="text-center py-24 bg-white rounded-[3rem] border border-slate-100 shadow-xl max-w-2xl mx-auto px-10">
                        <div className="w-20 h-20 bg-red-50 text-red-500 rounded-3xl flex items-center justify-center mx-auto mb-8">
                            <Eye className="w-10 h-10 opacity-50" />
                        </div>
                        <h3 className="text-2xl font-bold text-brand-dark mb-4">Oups !</h3>
                        <p className="text-slate-500 mb-8">{error}</p>
                        <Button variant="primary" onClick={() => window.location.reload()}>Réessayer</Button>
                    </div>
                ) : (
                    <div className="space-y-24">
                        {categories.map((category) => {
                            const allProducts = productsByCategory[category];
                            const visibleProducts = allProducts.slice(0, itemsPreviewCount);
                            
                            const cat = categoriesList.find(c => String(c.id) === String(category) || c.name === category);
                            const categoryDisplayName = cat ? cat.name : (category === 'null' || !category ? 'Général' : category);

                            return (
                                <div key={category} id={`category-${categoryDisplayName}`} className="relative">
                                    {/* Category Header */}
                                    <div className="flex items-center justify-between mb-10 border-b border-brand-dark/10 pb-6">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 bg-brand/10 rounded-2xl flex items-center justify-center text-brand">
                                                <div className="w-2 h-2 rounded-full bg-brand animate-pulse"></div>
                                            </div>
                                            <h3 className="text-3xl font-bold text-brand-dark">
                                                {categoryDisplayName}
                                            </h3>
                                            <span className="px-3 py-1 bg-brand-dark/5 text-brand-dark/60 text-xs font-bold rounded-full uppercase tracking-widest">
                                                {allProducts.length} Articles
                                            </span>
                                        </div>
                                    </div>

                                    {/* Products Grid */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10">
                                        {visibleProducts.map((product) => (
                                            <Card key={product.id} className="group h-full flex flex-col bg-white border border-slate-100 hover:shadow-2xl hover:shadow-brand-dark/10 transition-all duration-500 rounded-[2rem] overflow-hidden">
                                                <div className="relative aspect-[4/5] overflow-hidden">
                                                    <img
                                                        src={product.image}
                                                        alt={product.name}
                                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                                    />

                                                    {/* Stock Badge */}
                                                    {product.stock === 0 && (
                                                        <div className="absolute top-6 left-6 z-10">
                                                            <span className="px-4 py-2 bg-red-500 text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-full shadow-lg">
                                                                Épuisé
                                                            </span>
                                                        </div>
                                                    )}

                                                    {/* Quick Actions Overlay */}
                                                    <div className="absolute inset-0 bg-brand-dark/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-4">
                                                        <Link to={`/product/${product.id}`} className="w-12 h-12 bg-white text-brand-dark rounded-full flex items-center justify-center hover:bg-brand-dark hover:text-white transition-all transform translate-y-4 group-hover:translate-y-0 duration-500 shadow-xl">
                                                            <Eye className="w-5 h-5" />
                                                        </Link>
                                                        <button
                                                            onClick={() => toggleFavorite(product)}
                                                            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all transform translate-y-4 group-hover:translate-y-0 duration-500 delay-100 shadow-xl ${isFavorite(product.id)
                                                                ? 'bg-brand text-white'
                                                                : 'bg-white text-brand-dark hover:bg-brand-dark hover:text-white'
                                                                }`}
                                                        >
                                                            <Heart className={`w-5 h-5 ${isFavorite(product.id) ? 'fill-current' : ''}`} />
                                                        </button>
                                                        <button
                                                            onClick={() => product.stock > 0 && handleAddToCart(product)}
                                                            disabled={product.stock === 0}
                                                            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all transform translate-y-4 group-hover:translate-y-0 duration-500 delay-200 shadow-xl ${product.stock === 0 ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-white text-brand-dark hover:bg-brand-dark hover:text-white'}`}
                                                        >
                                                            <ShoppingCart className="w-5 h-5" />
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="p-8 flex flex-col flex-grow">
                                                    <div className="mb-6">
                                                        <Link to={`/product/${product.id}`}>
                                                            <h4 className="text-xl sm:text-2xl font-extrabold text-brand-dark group-hover:text-brand transition-colors leading-tight mb-3">
                                                                {product.name}
                                                            </h4>
                                                        </Link>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-2xl font-black text-brand">
                                                                {Math.floor(product.price).toLocaleString('fr-FR')} <span className="text-xs font-bold text-slate-400 uppercase">fcfa</span>
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div className="mt-auto">
                                                        <button
                                                            onClick={() => product.stock > 0 && handleAddToCart(product)}
                                                            disabled={product.stock === 0}
                                                            className={`w-full h-14 border-2 rounded-2xl text-sm font-bold transition-all duration-500 flex items-center justify-center gap-3 group/btn ${product.stock === 0
                                                                ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                                                                : 'bg-brand/5 text-brand-dark border-brand/10 hover:bg-brand-dark hover:text-white hover:border-brand-dark'
                                                                }`}
                                                        >
                                                            <ShoppingCart className="w-5 h-5 transition-transform group-hover/btn:scale-110" />
                                                            {product.stock === 0 ? 'Rupture de Stock' : 'Ajouter au Panier'}
                                                        </button>
                                                    </div>
                                                </div>
                                            </Card>
                                        ))}
                                    </div>

                                    {/* Voir plus button */}
                                    <div className="mt-12 flex justify-center">
                                        <Link
                                            to={`/category/${categoryDisplayName}`}
                                            className="inline-flex items-center gap-2 px-8 py-4 bg-white border border-brand-dark/10 text-brand-dark font-bold rounded-2xl hover:bg-brand hover:text-white hover:border-brand transition-all shadow-sm hover:shadow-xl group"
                                        >
                                            Voir plus de produits
                                            <ChevronRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                                        </Link>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <Toast
                message={toastMessage}
                isOpen={showToast}
                onClose={() => setShowToast(false)}
            />
        </div >
    );
};

export default CategoriesPage;
